<template>
  <div class="w-100 my-5">
    <div class="m-2 row box-white vertical-center d-flex justify-content-between">
      <div class="m-2 col-sm vertical-center">
        <span class="font-bold font-15 alpha-07">{{ $t('orders') }}:</span>
        <span class="mx-5 font-bold font-20 color-blue">{{ getDecimalFormat(orders) }}</span>
      </div>
      <div class="m-2 col-sm vertical-center">
        <span class="font-bold font-15 alpha-07">Gross Earnings:</span>
        <span class="mx-5 font-bold font-20 color-blue">{{ getPriceFormat(handlingFee) }}</span>
      </div>
      <div class="m-2 col-sm vertical-center">
        <span class="font-bold font-15 alpha-07">Net Earnings:</span>
        <span class="mx-5 font-bold font-20 color-blue">{{ getPriceFormat(handlingFee - payPalFee - stripeFee - sumUpFee) }}</span>
      </div>
      <div class="m-2 col-sm vertical-center">
        <span class="font-bold font-15 alpha-07">Salary:</span>
        <span class="mx-5 font-bold font-20 color-blue">{{ getPriceFormat((handlingFee - payPalFee - stripeFee - sumUpFee) * SALARY_PERCENT) }}</span>
      </div>
    </div>
    <div class="m-2 row box-white vertical-center d-flex justify-content-between">
      <div class="m-2 col-sm vertical-center d-flex justify-content-center">
        <span :class="`${isMobile ? '' : 'ml-auto'} mr-5 font-12 font-bold`">{{ $t('select_year') }}</span>
        <select :class="isMobile ? 'mr-auto' : ''" v-model="selectedYear">
          <option :value="year" v-for="year in years" :key="year" :selected="year == selectedYear">{{ year }}</option>
        </select>
      </div>
      <div class="m-2 col-sm vertical-center d-flex justify-content-center">
        <span class="mr-5 font-12 font-bold">{{ $t('select_month') }}</span>
        <select class="mr-auto" v-model="selectedMonth">
          <option :value="month" v-for="month in months" :key="month" :selected="month == selectedMonth">{{ getMonthLabel(month) }}</option>
        </select>
      </div>
      <div class="m-2 col-sm">
        <v-btn class="ml-auto button-normal" @click="generateReport()">
          <span>{{ $t('report') }}</span>
        </v-btn>
      </div>
    </div>
    <div class="row mx-auto">
      <div class="col-sm m-2 box-white d-flex flex-column">
        <span class="fa fa-euro-sign font-15 color-blue">&nbsp;&nbsp;&nbsp;{{ $t('total_price') }}</span>
        <span :class="`mt-3 font-bold ${isMobile ? 'font-30' : 'font-50'} color-blue`">{{ getPriceFormat(totalPrice) }}</span>
        <div>
          <span class="font-15 color-red">{{ getPriceFormat(payPalFee) }}</span>
          <span class="ml-2 font-12 alpha-07">PayPal Fee</span>
        </div>
        <div>
          <span class="font-15 color-red">{{ getPriceFormat(stripeFee) }}</span>
          <span class="ml-2 font-12 alpha-07">Stripe Fee</span>
        </div>
        <div>
          <span class="font-15 color-red">{{ getPriceFormat(sumUpFee) }}</span>
          <span class="ml-2 font-12 alpha-07">SumUp Fee</span>
        </div>
      </div>
      <div class="col-sm m-2 box-white d-flex flex-column">
        <span class="fa fa-euro-sign font-15 color-blue">&nbsp;&nbsp;&nbsp;{{ $t('handling_fee') }}</span>
        <span :class="`mt-3 font-bold ${isMobile ? 'font-30' : 'font-50'} color-blue`">{{ getPriceFormat(handlingFee) }}</span>
      </div>
    </div>
    <vue-html2pdf
      :show-layout="false"
      :float-layout="true"
      :enable-download="true"
      :preview-modal="false"
      :paginate-elements-by-height="1900"
      :pdf-quality="2"
      :manual-pagination="true"
      :html-to-pdf-options="htmlToPdfOptions"
      pdf-format="a4"
      pdf-orientation="portrait"
      pdf-content-width="100%"
      ref="html2Pdf"
    >
      <section slot="pdf-content">
        <div class="d-flex flex-column">
          <div class="d-flex justify-content-end">
            <img :src="assets.logo" :height="30" />
          </div>
          <span class="my-10 font-20 font-bold color-black">Drink Report - {{ getReportLabel() }}</span>
          <div class="mt-5 d-flex justify-content-between">
            <span class="font-15 color-black">{{ $t('orders') }}</span>
            <span class="font-15 font-bold color-black">{{ getDecimalFormat(orders) }}</span>
          </div>
          <div class="mt-5 d-flex justify-content-between">
            <span class="font-15 color-black">Gross Earnings</span>
            <span class="font-15 font-bold color-black">{{ getPriceFormat(handlingFee) }}</span>
          </div>
          <div class="mt-5 d-flex justify-content-between">
            <span class="font-15 color-black">Net Earnings</span>
            <span class="font-15 font-bold color-black">{{ getPriceFormat(handlingFee - payPalFee - stripeFee) }}</span>
          </div>
          <div class="mt-5 ml-2 d-flex justify-content-between">
            <span class="font-15 color-black">{{ $t('total_price') }}</span>
            <span class="font-15 font-bold color-black">{{ getPriceFormat(totalPrice) }}</span>
          </div>
          <div class="mt-2 ml-5 d-flex justify-content-between">
            <span class="font-15 color-black">PayPal Fee</span>
            <span class="font-15 font-bold color-black">{{ getPriceFormat(payPalFee) }}</span>
          </div>
          <div class="mt-2 ml-5 d-flex justify-content-between">
            <span class="font-15 color-black">Stripe Fee</span>
            <span class="font-15 font-bold color-black">{{ getPriceFormat(stripeFee) }}</span>
          </div>
          <div class="mt-5 ml-2 d-flex justify-content-between">
            <span class="font-15 color-black">{{ $t('handling_fee') }}</span>
            <span class="font-15 font-bold color-black">{{ getPriceFormat(handlingFee) }}</span>
          </div>
        </div>
      </section>
    </vue-html2pdf>
  </div>
</template>

<script>
import VueHtml2pdf from 'vue-html2pdf';

import { firestore } from '../../../main';
import { collection, query, orderBy, startAfter, endBefore, getDocs, Timestamp } from 'firebase/firestore';
import { getPriceFormat, getDecimalFormat, showLoading, getDateStringFromDate, zeroPad } from '../../../functions';

import logo from '@/assets/image/logo_for_invoice.png';

export default {
  name: 'DrinkReport',
  components: {
    VueHtml2pdf
  },
  computed: {
    isMobile() {
      return this.$store.state.isMobile;
    }
  },
  watch: {
    selectedYear() {
      this.getDrinkOrderListTask();
    },
    selectedMonth() {
      this.getDrinkOrderListTask();
    }
  },
  data() {
    const curDate = new Date();
    return {
      assets: {
        logo
      },
      htmlToPdfOptions: {
        margin: 10,
        filename: 'DrinkReport',
        image: {
          type: 'jpeg',
          quality: 1.0
        },
        html2canvas: {
          scale: 5,
          useCORS: true
        }
      },
      orders: 0,
      totalPrice: 0,
      handlingFee: 0,
      payPalFee: 0,
      stripeFee: 0,
      sumUpFee: 0,
      selectedYear: curDate.getFullYear(),
      selectedMonth: curDate.getMonth(),
      startDate: curDate,
      years: [],
      months: []
    }
  },
  mounted() {
    const currentYear = new Date().getFullYear();
    for (var year=2023; year<=currentYear; year++) {
      this.years.push(year);
    }
    for (var month=-1; month<=11; month++) {
      this.months.push(month);
    }
    this.getDrinkOrderListTask();
  },
  methods: {
    getPriceFormat,
    getDecimalFormat,
    getMonthLabel(month) {
      return month < 0 ? 'All' : (month + 1);
    },
    getReportLabel() {
      return this.selectedMonth < 0 ? this.selectedYear : getDateStringFromDate(this.startDate, 'MMMM YYYY')
    },
    generateReport() {
      if (this.selectedMonth < 0) {
        this.htmlToPdfOptions.filename = `DrinkReport_${this.selectedYear}`;
      } else {
        this.htmlToPdfOptions.filename = `DrinkReport_${this.selectedYear}_${zeroPad(this.selectedMonth + 1, 2)}`;
      }
      this.$refs.html2Pdf.generatePdf()
    },
    getDrinkOrderListTask() {
      // Helper function to create a date at midnight in Germany timezone (Europe/Berlin)
      const createGermanyMidnight = (year, month, day) => {
        // Create a UTC date
        const date = new Date(Date.UTC(year, month, day, 0, 0, 0));
        // Get the date string in Germany timezone
        const germanyStr = date.toLocaleString('sv-SE', { timeZone: 'Europe/Berlin' });
        // Get the date string in UTC
        const utcStr = date.toLocaleString('sv-SE', { timeZone: 'UTC' });
        // Calculate the offset between UTC and Germany time
        const offset = new Date(utcStr).getTime() - new Date(germanyStr).getTime();
        // Return date adjusted for Germany timezone
        return new Date(Date.UTC(year, month, day, 0, 0, 0) - offset);
      };

      // Create start date in Germany timezone
      if (this.selectedMonth < 0) {
        this.startDate = createGermanyMidnight(this.selectedYear, 0, 1);
      } else {
        this.startDate = createGermanyMidnight(this.selectedYear, this.selectedMonth, 1);
      }

      // Create end date in Germany timezone
      const endDate = this.selectedMonth < 0
        ? createGermanyMidnight(this.selectedYear + 1, 0, 1)
        : createGermanyMidnight(this.selectedYear, this.selectedMonth + 1, 1);

      const startTimestamp = Timestamp.fromDate(this.startDate);
      const endTimestamp = Timestamp.fromDate(endDate);
      const loader = showLoading(this);
      const q = query(collection(firestore, 'drinkOrder'), orderBy('createdAt', 'asc'), startAfter(startTimestamp), endBefore(endTimestamp));
      getDocs(q).then(querySnapshot => {
        loader.hide();
        var orders = 0;
        var totalPrice = 0;
        var handlingFee = 0;
        var payPalFee = 0;
        var stripeFee = 0;
        var sumUpFee = 0;
        querySnapshot.forEach(doc => {
          const data = doc.data();
          if (data.totalPrice > 0 && !data.eventId.startsWith(this.TEST_PREFIX)) {
            orders++;
            totalPrice += data.totalPrice || 0;
            handlingFee += data.handlingFee || 0;
            if (data.orderId.startsWith(this.PAYMENT_TYPE_PAYPAL)) {
              payPalFee += data.totalPrice * 0.0249;
            } else if (data.orderId.startsWith(this.PAYMENT_TYPE_STRIPE)) {
              stripeFee += data.totalPrice * 0.015;
            } else if (data.orderId.startsWith(this.PAYMENT_TYPE_CARD)) {
              sumUpFee += data.drinkPrice * 0.0095;
            }
          }
        });
        this.orders = orders;
        this.totalPrice = totalPrice;
        this.handlingFee = handlingFee;
        this.payPalFee = payPalFee;
        this.stripeFee = stripeFee;
        this.sumUpFee = sumUpFee;
      }).catch(error => {
        loader.hide();
        this.$toast.error('Unexpected error occurs - ' + error.message);
      });
    }
  }
};
</script>